import { Component, Event, EventEmitter, Host, Listen, Prop, State, h } from '@stencil/core';

import { getLocalizedStrings } from '../../utils/utils';
import rpfAuthStrings from './locales/translations.json';

@Component({
  tag: 'rpf-auth',
  styleUrl: 'rpf-auth.scss',
  shadow: true,
})
export class RpfAuth {
  @Prop() locale: string = 'en-GB';
  @Prop() loggedIn: boolean;
  @Prop() authAnchors: boolean = false;
  @Prop() logInToken: string;
  @Prop() signUpToken: string;
  @Prop() logInTokenUrl: string;
  @Prop() signUpTokenUrl: string;
  @Prop() logInPath: string;
  @Prop() logOutPath: string;
  @Prop() signUpPath: string;
  @Prop() hideSignUp: boolean;
  @Prop() hideLogIn: boolean;
  @Prop() hideAccountDropdown: boolean;
  @Prop() mobileMenu: boolean = false;
  @Prop({ reflect: true }) theme: string;

  @Event() logIn: EventEmitter<{ event: globalThis.Event }>;
  @Event() logOut: EventEmitter<{ event: globalThis.Event }>;
  @Event() signUp: EventEmitter<{ event: globalThis.Event }>;
  @Event() yourAccount: EventEmitter<{ event: globalThis.Event }>;

  @State() isOpen = false;
  @State() fetchedLogInToken: string;
  @State() fetchedSignUpToken: string;

  authStrings: any;
  accountLink: string = 'https://my.raspberrypi.org/profile/';

  async componentWillLoad() {
    if (this.logInTokenUrl) {
      this.fetchedLogInToken = await this.fetchToken(this.logInTokenUrl);
    }
    if (this.signUpTokenUrl) {
      this.fetchedSignUpToken = await this.fetchToken(this.signUpTokenUrl);
    }
  }

  async componentWillRender() {
    this.authStrings = getLocalizedStrings(this.locale, rpfAuthStrings);
  }

  async fetchToken(url: string): Promise<string> {
    try {
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        return data.authenticity_token;
      } else {
        console.error(`Failed to fetch token from ${url}`);
        return null;
      }
    } catch (error) {
      console.error(`Error fetching token from ${url}:`, error);
      return null;
    }
  }

  @Listen('isOpenChange')
  isOpenChangeHandler(event: CustomEvent<{ isOpen: boolean; char?: string }>) {
    this.isOpen = event.detail.isOpen;
  }

  onLogInHandler = event => {
    this.logIn.emit({ event: event });
  };

  onSignUpHandler = event => {
    this.signUp.emit({ event: event });
  };

  onAccountHandler = event => {
    this.yourAccount.emit({ event: event });
  };

  onLogOutHandler = event => {
    this.logOut.emit({ event: event });
  };

  upArrowIcon() {
    return (
      <svg fill="none" height="7" viewBox="0 0 10 7" width="10" xmlns="http://www.w3.org/2000/svg">
        <path d="m1.16667 6.81251-1.16667-1.16667 5-4.999996 5 4.999996-1.16667 1.16667-3.83333-3.83333z" fill="#212121" />
      </svg>
    );
  }

  downArrowIcon() {
    return (
      <svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 6.81251L0 1.81251L1.16667 0.645844L5 4.47918L8.83333 0.645844L10 1.81251L5 6.81251Z" fill="#212121" />
      </svg>
    );
  }

  externalLinkIcon() {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
        <path
          d="M2 0C0.906937 0 0 0.906937 0 2V16C0 17.0931 0.906937 18 2 18H16C17.0931 18 18 17.0931 18 16V9H16V16H2V2H9V0H2ZM11 0V2H14.5859L5.29297 11.293L6.70703 12.707L16 3.41406V7H18V0H11Z"
          fill="#212121"
        />
      </svg>
    );
  }

  accountIcon() {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
          d="M4.87484 14.25C5.58317 13.7083 6.37484 13.2811 7.24984 12.9683C8.12484 12.6561 9.0415 12.5 9.99984 12.5C10.9582 12.5 11.8748 12.6561 12.7498 12.9683C13.6248 13.2811 14.4165 13.7083 15.1248 14.25C15.6109 13.6805 15.9896 13.0347 16.2607 12.3125C16.5312 11.5903 16.6665 10.8194 16.6665 9.99999C16.6665 8.15277 16.0173 6.57971 14.719 5.28082C13.4201 3.98249 11.8471 3.33332 9.99984 3.33332C8.15262 3.33332 6.57984 3.98249 5.2815 5.28082C3.98261 6.57971 3.33317 8.15277 3.33317 9.99999C3.33317 10.8194 3.46873 11.5903 3.73984 12.3125C4.01039 13.0347 4.38873 13.6805 4.87484 14.25ZM9.99984 10.8333C9.18039 10.8333 8.48928 10.5522 7.9265 9.98999C7.36428 9.42721 7.08317 8.7361 7.08317 7.91666C7.08317 7.09721 7.36428 6.4061 7.9265 5.84332C8.48928 5.2811 9.18039 4.99999 9.99984 4.99999C10.8193 4.99999 11.5104 5.2811 12.0732 5.84332C12.6354 6.4061 12.9165 7.09721 12.9165 7.91666C12.9165 8.7361 12.6354 9.42721 12.0732 9.98999C11.5104 10.5522 10.8193 10.8333 9.99984 10.8333ZM9.99984 18.3333C8.84706 18.3333 7.76373 18.1144 6.74984 17.6767C5.73595 17.2394 4.854 16.6458 4.104 15.8958C3.354 15.1458 2.76039 14.2639 2.32317 13.25C1.88539 12.2361 1.6665 11.1528 1.6665 9.99999C1.6665 8.84721 1.88539 7.76388 2.32317 6.74999C2.76039 5.7361 3.354 4.85416 4.104 4.10416C4.854 3.35416 5.73595 2.76027 6.74984 2.32249C7.76373 1.88527 8.84706 1.66666 9.99984 1.66666C11.1526 1.66666 12.2359 1.88527 13.2498 2.32249C14.2637 2.76027 15.1457 3.35416 15.8957 4.10416C16.6457 4.85416 17.2393 5.7361 17.6765 6.74999C18.1143 7.76388 18.3332 8.84721 18.3332 9.99999C18.3332 11.1528 18.1143 12.2361 17.6765 13.25C17.2393 14.2639 16.6457 15.1458 15.8957 15.8958C15.1457 16.6458 14.2637 17.2394 13.2498 17.6767C12.2359 18.1144 11.1526 18.3333 9.99984 18.3333ZM9.99984 16.6667C10.7359 16.6667 11.4304 16.5592 12.0832 16.3442C12.7359 16.1286 13.3332 15.8194 13.8748 15.4167C13.3332 15.0139 12.7359 14.7047 12.0832 14.4892C11.4304 14.2742 10.7359 14.1667 9.99984 14.1667C9.26373 14.1667 8.56928 14.2742 7.9165 14.4892C7.26373 14.7047 6.6665 15.0139 6.12484 15.4167C6.6665 15.8194 7.26373 16.1286 7.9165 16.3442C8.56928 16.5592 9.26373 16.6667 9.99984 16.6667ZM9.99984 9.16666C10.3609 9.16666 10.6596 9.0486 10.8957 8.81249C11.1318 8.57638 11.2498 8.27777 11.2498 7.91666C11.2498 7.55555 11.1318 7.25693 10.8957 7.02082C10.6596 6.78471 10.3609 6.66666 9.99984 6.66666C9.63873 6.66666 9.34012 6.78471 9.104 7.02082C8.86789 7.25693 8.74984 7.55555 8.74984 7.91666C8.74984 8.27777 8.86789 8.57638 9.104 8.81249C9.34012 9.0486 9.63873 9.16666 9.99984 9.16666Z"
          fill="#212121"
        />
      </svg>
    );
  }

  yourAccountMenu() {
    return (
      <div class={`rpf-auth__menu ${this.mobileMenu ? 'rpf-auth__menu--responsive-menu' : ''}`}>
        <ul>
          <li>
            <a onClick={e => this.onAccountHandler(e)} href={this.accountLink}>
              <span>{this.authStrings['account']}</span>
              <span>{this.externalLinkIcon()}</span>
            </a>
          </li>
          <li>
            <a onClick={e => this.onLogOutHandler(e)} href={this.logOutPath || '#'}>
              {this.authStrings['logout']}
            </a>
          </li>
        </ul>
      </div>
    )
  }

  yourAccountDropdown() {
    return (
      <rpf-dropdown align-menu="start" theme={this.theme} list-box-id="rpf-auth__listbox">
        <div slot="button">
          <div class="rpf-auth__your_account_dropdown">
            <span class="rpf-auth__your_account_dropdown__account_icon">{this.accountIcon()}</span>
            <span class="rpf-auth__your_account_dropdown__text">{this.authStrings['account']}</span>
            <span class="rpf-auth__your_account_dropdown__arrow_icon">{this.isOpen ? this.upArrowIcon() : this.downArrowIcon()}</span>
          </div>
        </div>
      {this.yourAccountMenu()}
      </rpf-dropdown>
    );
  }

  logOutButton() {
    return (
      <div slot="button">
        <div class="rpf-auth__buttons">
          <div class="rpf-auth__log-out-button">
            <form method="GET" action={this.logOutPath}>
              <button onClick={e => this.onLogOutHandler(e)} class="rpf-button rpf-button--sm rpf-button--secondary">
                {this.authStrings['logout']}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  signUpAnchor() {
    return (
      <div class="rpf-auth__sign-up-button">
        <a href={this.signUpPath} onClick={e => this.onSignUpHandler(e)} class="rpf-button rpf-button--sm rpf-button--tertiary">
          {this.authStrings['signup']}
        </a>
      </div>
    );
  }

  signUpButton() {
    return (
      <div class="rpf-auth__sign-up-button">
        <form method="POST" action={this.signUpPath}>
          <button onClick={e => this.onSignUpHandler(e)} type="submit" class="rpf-button rpf-button--sm rpf-button--tertiary">
            {this.authStrings['signup']}
          </button>
          <input type="hidden" name="authenticity_token" value={this.fetchedSignUpToken || this.signUpToken} autocomplete="off" />
        </form>
      </div>
    );
  }

  logInAnchor() {
    return (
      <div class="rpf-auth__log-in-button">
        <a href={this.logInPath} onClick={e => this.onLogInHandler(e)} class="rpf-auth__log-in-button--lg rpf-button rpf-button--sm rpf-button--secondary">
          {this.authStrings['login']}
        </a>
        <a href={this.logInPath} onClick={e => this.onLogInHandler(e)} class="rpf-auth__log-in-button--sm">
          {this.accountIcon()}
        </a>
      </div>
    );
  }

  logInButton() {
    return (
      <div class="rpf-auth__log-in-button">
        <form method="POST" action={this.logInPath}>
          <button onClick={e => this.onLogInHandler(e)} class="rpf-auth__log-in-button--lg rpf-button rpf-button--sm rpf-button--secondary">
            {this.authStrings['login']}
          </button>
          <button onClick={e => this.onLogInHandler(e)} class="rpf-auth__log-in-button--sm">
            {this.accountIcon()}
          </button>
          <input type="hidden" name="authenticity_token" value={this.fetchedLogInToken || this.logInToken} autocomplete="off" />
        </form>
      </div>
    );
  }

  loggedInSlot() {
    return (
      this.hideAccountDropdown ? this.logOutButton() :
        this.mobileMenu ? this.yourAccountMenu() : this.yourAccountDropdown()
    )
  }

  loggedOutSlot() {
    return (
      <div slot="button">
        <div class={`rpf-auth__buttons ${this.mobileMenu ? 'rpf-auth__buttons--responsive-menu' : ''}`}>
          {this.hideSignUp ? null : this.authAnchors ? this.signUpAnchor() : this.signUpButton()}
          {this.hideLogIn ? null : this.authAnchors ? this.logInAnchor() : this.logInButton()}
        </div>
      </div>
    );
  }

  render() {
    return <Host>{this.loggedIn ? this.loggedInSlot() : this.loggedOutSlot()}</Host>;
  }
}
